"use client";

import { c as _c } from "react/compiler-runtime";
import { Paperclip } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useIntl } from "react-intl";
import FileUploadHc from "assets/icons/file-upload-hc.svg";
import FileUpload from "assets/icons/file-upload.svg";
import FileUploadedHc from "assets/icons/file-uploaded-hc.svg";
import FileUploaded from "assets/icons/file-uploaded.svg";
import type { CvFileProps } from "ui/FileInput/types";
import { TypoTextBold } from "ui/Typo";
import { useFormField } from "uiShadcn/form";
import { cn } from "utils/helpers/cn";
import useHighContrast from "utils/hooks/useHighContrast";
export interface CvFileDescriptionProps extends Pick<CvFileProps, "disabled" | "hasError"> {
  isDragOver: boolean;
  file: File | null;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
}
export default function FileStatus(t0) {
  const $ = _c(30);
  const {
    isDragOver,
    file,
    onKeyDown,
    disabled,
    hasError
  } = t0;
  const [isHighContrast] = useHighContrast();
  const UploadIcon = isHighContrast ? FileUploadHc : FileUpload;
  const UploadedIcon = isHighContrast ? FileUploadedHc : FileUploaded;
  const {
    formatMessage
  } = useIntl();
  const {
    formDescriptionId,
    formMessageId
  } = useFormField();
  let t1;
  if ($[0] !== file || $[1] !== formatMessage || $[2] !== isDragOver) {
    t1 = () => {
      switch (true) {
        case isDragOver:
          {
            return <span className="text-blue-950 dark:text-font-contrast">{formatMessage({
                id: "dropHere"
              })}</span>;
          }
        case !!file:
          {
            return <span className="flex gap-1 font-semibold text-font-contrast"><Paperclip size={20} />{formatMessage({
                id: "change_file"
              })}</span>;
          }
        default:
          {
            return <><span className="font-semibold text-font-contrast">{formatMessage({
                  id: "addFile"
                })}</span>{" "}{formatMessage({
                id: "orDropFromDevice"
              })}</>;
          }
      }
    };
    $[0] = file;
    $[1] = formatMessage;
    $[2] = isDragOver;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const renderFileStatusDisplay = t1;
  const t2 = !!file;
  let t3;
  if ($[4] !== hasError || $[5] !== isDragOver || $[6] !== t2) {
    t3 = cn("inset-0 flex w-full flex-col items-center justify-center gap-n4 bg-background px-n4 py-n6 text-center", "rounded-md border border-blue-800 border-dashed dark:border-foreground", "data-[disable=true]:cursor-not-allowed", "focus-visible:outline-hidden", "transition-all duration-200", {
      "bg-blue-500/30": isDragOver,
      "dark:border-foreground/80 dark:bg-foreground/10": isDragOver,
      "border-solid": t2,
      "border-destructive dark:border-destructive": hasError
    });
    $[4] = hasError;
    $[5] = isDragOver;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = `${formDescriptionId} ${formMessageId}`;
  let t5;
  if ($[8] !== isDragOver) {
    t5 = cn("flex max-w-72 items-center gap-n2 text-left transition-opacity duration-400", {
      "opacity-0": isDragOver
    });
    $[8] = isDragOver;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== UploadIcon || $[11] !== UploadedIcon || $[12] !== file) {
    t6 = file ? <UploadedIcon /> : <UploadIcon />;
    $[10] = UploadIcon;
    $[11] = UploadedIcon;
    $[12] = file;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== file) {
    t7 = file?.name && <TypoTextBold className="line-clamp-2 flex-1">{file.name}</TypoTextBold>;
    $[14] = file;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== renderFileStatusDisplay) {
    t9 = <p className="text-neutral-700 dark:text-font-1">{renderFileStatusDisplay()}</p>;
    $[20] = renderFileStatusDisplay;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== disabled || $[23] !== hasError || $[24] !== onKeyDown || $[25] !== t3 || $[26] !== t4 || $[27] !== t8 || $[28] !== t9) {
    t10 = <div data-disable={disabled} aria-disabled={disabled} className={t3} role="button" tabIndex={0} onKeyDown={onKeyDown} data-test-id="file-status" aria-describedby={t4} aria-invalid={hasError}>{t8}{t9}</div>;
    $[22] = disabled;
    $[23] = hasError;
    $[24] = onKeyDown;
    $[25] = t3;
    $[26] = t4;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}